import { CycleInfo, fmtDate } from "@/components/billing/CycleInfo";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, PageHeader } from "@/components/app/StatusBadge";
import { api } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { PlanCheckoutButton } from "@/components/billing/PlanCheckout";
import { SubscriptionCard } from "@/components/billing/SubscriptionCard";
import { CycleToggle, PlanCards } from "@/components/billing/PlanCards";
import { TopupCard } from "@/components/billing/TopupCard";
import { AutoTopupCard } from "@/components/billing/AutoTopupCard";

const Usage = ({ id, label, used, quota }) => (
  <Card className="p-5" data-testid={`billing-usage-${id}`}>
    <p className="text-sm font-semibold">{label}</p>
    <p className="mt-1 font-heading text-3xl font-extrabold">{used}<span className="text-base font-medium text-slate-400"> / {quota}</span></p>
    <Progress value={quota ? Math.min(100, (used / quota) * 100) : 0} className="mt-3 h-2" />
  </Card>
);

export default function Billing() {
  const { L, lang } = useApp();
  const { workspace, refresh } = useAuth();
  const [u, setU] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const load = () => api.get("/usage", { params: { workspace_id: workspace.id } }).then((r) => setU(r.data));
  useEffect(() => { if (workspace) load(); }, [workspace]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!u) return null;
  const canManage = ["owner", "admin"].includes(workspace.role);
  const free = u.plan.key === "free";
  const sub = u.subscription && u.subscription.status !== "canceled" ? u.subscription : null;
  const isCurrent = (p) => p.key === u.plan.key && (!sub || p.key === "free" || sub.cycle === cycle);
  const action = (p) => (isCurrent(p) || p.key === "free"
    ? <Button disabled className="mt-6 w-full rounded-xl" data-testid={`upgrade-${p.key}-btn`}>{isCurrent(p) ? L("Pelan semasa", "Current plan") : sub ? L("Batal langganan di atas", "Cancel subscription above") : L("Pelan asas", "Base plan")}</Button>
    : <PlanCheckoutButton plan={p} current={u.plan} cycle={cycle} workspaceId={workspace.id} canManage={canManage} hasSub={sub?.source === "stripe"} onChanged={() => { load(); refresh?.(); }} />);
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8" data-testid="billing-page">
      <PageHeader title={L("Langganan & Penggunaan", "Billing & Usage")} desc={`${workspace.name} · ${L("pelan", "plan")} ${u.plan.name} · ${free ? L("transkripsi seumur hidup", "lifetime transcription") : `${L("kitaran", "cycle")} ${fmtDate(u.cycle_start, lang)} – ${fmtDate(u.cycle_end, lang)}`}`} />
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {free
          ? <Usage id="free" label={L("Transkripsi percuma digunakan", "Free transcriptions used")} used={u.free_used ?? 0} quota={u.plan.lifetime_transcriptions} />
          : <Usage id="minutes" label={L("Minit transkripsi", "Transcription minutes")} used={u.minutes_used} quota={u.minutes_quota} />}
        {u.credits_quota > 0 && <Usage id="credits" label={L("Jana dokumen", "Document generations")} used={u.credits_used} quota={u.credits_quota} />}
      </div>
      <CycleInfo u={u} sub={sub} />
      <TopupCard u={u} workspaceId={workspace.id} canManage={canManage} onDone={(d) => setU((x) => ({ ...x, ...d }))} />
      {u.plan.key !== "free" && <AutoTopupCard workspaceId={workspace.id} canManage={canManage} />}
      <SubscriptionCard sub={sub} plan={u.plan} workspaceId={workspace.id} canManage={canManage} onDone={load} />
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold">{L("Pelan", "Plans")}</h2>
        <CycleToggle value={cycle} onChange={setCycle} />
      </div>
      <div className="mt-5"><PlanCards plans={u.plans} cycle={cycle} current={u.plan.key} action={action} /></div>
      <p className="mt-3 text-xs text-slate-500">{L("Harga dalam RM, belum termasuk SST jika berkenaan. Pembayaran selamat melalui kad (Stripe) atau PayPal; langganan diperbaharui automatik dan boleh dibatalkan bila-bila masa. Minit Mesyuarat Online (bot) dikira 1.5× minit biasa.", "Prices in RM, excluding SST where applicable. Secure payment by card (Stripe) or PayPal; subscriptions auto-renew and can be cancelled anytime. Meeting bot minutes count as 1.5× regular minutes.")}</p>
      <Card className="mt-6 p-5">
        <h2 className="font-semibold">{L("Rekod penggunaan", "Usage events")}</h2>
        <div className="slim-scroll mt-3 max-h-72 divide-y divide-slate-100 overflow-y-auto pr-2 text-sm" data-testid="usage-events">
          {u.events.length === 0 && <p className="py-3 text-slate-500">{L("Tiada penggunaan lagi.", "No usage yet.")}</p>}
          {u.events.map((e) => (
            <div key={e.id} className="flex justify-between py-2"><span>{e.type === "ai_credits" ? L("Jana dokumen", "Document generation") : L("Minit transkripsi", "Transcription minutes")}</span><span className="text-slate-500">{e.amount} · {new Date(e.created_at).toLocaleString(lang === "en" ? "en-MY" : "ms-MY")}</span></div>
          ))}
        </div>
      </Card>
    </div>
  );
}
